// /////////////////////////////////////////////////////////////
// [ファイル] TurquoiseFilterPanel.tsx
// [概要] ターコイズ取引先の詳細検索パネル (旧画面 accounting/tq_partner_list.js と同構成)。
//        取引先一覧と取込レビューの両方から使う。
//        入力中の値はこのコンポーネント内に閉じ込め、「検索」押下時のみ親へ通知する。
// /////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import {
  Typography,
  Button,
  Stack,
  Tooltip,
  TextField,
  IconButton,
  Popover,
  Grid,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  FormControl,
} from '@mui/material';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import {
  MatchSearchFilters,
  MembershipFilter,
  EMPTY_FILTERS,
  hasActiveFilters,
} from '../hooks/useTurquoiseMatchPreview';

/**
 * 詳細検索パネル (旧画面 tq_partner_list.js と同構成)。
 * 入力中の値はこのコンポーネント内に閉じ込め、「検索」押下時のみ親へ通知する。
 *
 * variant で見た目を切り替える。
 *   'button' 独立したボタン (取込レビュー)
 *   'icon'   検索欄の中に収めるアイコン (取引先一覧)
 *
 * showMakeDate で登録日の範囲欄を出す。登録日はターコイズ本体 (MAKE_DATE) にしか無く、
 * 取込レビュー (Cloud SQL の名寄せ結果) では絞り込めないため取引先一覧でのみ有効にする。
 */
const TurquoiseFilterPanel = React.memo(function TurquoiseFilterPanel({
  filters,
  onApply,
  disabled,
  variant = 'button',
  showMakeDate = false,
}: {
  filters: MatchSearchFilters;
  onApply: (f: MatchSearchFilters) => void;
  disabled?: boolean;
  variant?: 'button' | 'icon';
  showMakeDate?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<MatchSearchFilters>(filters);

  // パネルを開くたびに現在の条件を下書きへ写す
  const open = (e: React.MouseEvent<HTMLElement>) => {
    setDraft(filters);
    setAnchorEl(e.currentTarget);
  };
  const close = () => setAnchorEl(null);

  const set = <K extends keyof MatchSearchFilters>(key: K, value: MatchSearchFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  // 登録日の開始と終了が逆転している間は検索させない (旧画面と同じチェック)
  const dateReversed =
    draft.make_date_start !== '' && draft.make_date_end !== '' &&
    draft.make_date_start > draft.make_date_end;

  const apply = () => {
    if (dateReversed) return;
    onApply(draft);
    close();
  };

  const dateField = (label: string, key: 'make_date_start' | 'make_date_end') => (
    <TextField
      size="small"
      fullWidth
      type="date"
      label={label}
      value={draft[key]}
      onChange={(e) => set(key, e.target.value)}
      error={dateReversed}
      InputLabelProps={{ shrink: true }}
    />
  );

  const active = hasActiveFilters(filters);
  const field = (label: string, key: keyof MatchSearchFilters, placeholder?: string) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      placeholder={placeholder}
      value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as never)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          apply();
        }
      }}
    />
  );

  return (
    <>
      <Tooltip title="詳細検索">
        <span>
          {variant === 'icon' ? (
            <IconButton
              size="small"
              onClick={open}
              disabled={disabled}
              aria-label="詳細検索"
              sx={{ p: 0.5, color: active ? 'primary.main' : 'text.secondary' }}
            >
              <Icons.FilterList sx={{ fontSize: 18 }} />
            </IconButton>
          ) : (
            <Button
              size="small"
              variant={active ? 'contained' : 'outlined'}
              startIcon={<Icons.FilterList sx={{ fontSize: 18 }} />}
              onClick={open}
              disabled={disabled}
              sx={{ ...STANDARD_TEXT_BUTTON_SX, minWidth: 104 }}
            >
              詳細検索
            </Button>
          )}
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableScrollLock
        PaperProps={{ sx: { width: 520, p: 2 } }}
      >
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1.5 }}>検索条件</Typography>
        <Grid container spacing={1.5}>
          <Grid size={6}>{field('開始コード', 'code_start', '例: 10000')}</Grid>
          <Grid size={6}>{field('終了コード', 'code_end', '例: 20999')}</Grid>
          {showMakeDate && (
            <>
              <Grid size={6}>{dateField('登録日 (開始)', 'make_date_start')}</Grid>
              <Grid size={6}>{dateField('登録日 (終了)', 'make_date_end')}</Grid>
              {dateReversed && (
                <Grid size={12}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'error.main', mt: -0.5 }}>
                    登録日の開始と終了が逆転しています
                  </Typography>
                </Grid>
              )}
            </>
          )}
          <Grid size={12}>{field('会社名', 'company_name')}</Grid>
          <Grid size={12}>{field('住所', 'address')}</Grid>
          <Grid size={4}>{field('電話番号', 'tel')}</Grid>
          <Grid size={4}>{field('ＦＡＸ番号', 'fax')}</Grid>
          <Grid size={4}>{field('携帯電話', 'mobile')}</Grid>
          <Grid size={12}>
            <FormControl>
              <FormLabel sx={{ fontSize: '0.75rem' }}>会員情報</FormLabel>
              <RadioGroup
                row
                value={draft.membership}
                onChange={(e) => set('membership', e.target.value as MembershipFilter)}
              >
                <FormControlLabel value="all" control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.8rem' }}>すべて表示</Typography>} />
                <FormControlLabel value="member" control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.8rem' }}>会員</Typography>} />
                <FormControlLabel value="regular" control={<Radio size="small" />}
                  label={<Typography sx={{ fontSize: '0.8rem' }}>正会員</Typography>} />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid size={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={draft.hide_deleted}
                  onChange={(e) => set('hide_deleted', e.target.checked)}
                />
              }
              label={<Typography sx={{ fontSize: '0.8rem' }}>削除非表示</Typography>}
            />
          </Grid>
          <Grid size={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={draft.kana_sort}
                  onChange={(e) => set('kana_sort', e.target.checked)}
                />
              }
              label={<Typography sx={{ fontSize: '0.8rem' }}>五十音順表示</Typography>}
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setDraft(EMPTY_FILTERS)} sx={STANDARD_TEXT_BUTTON_SX}>
            条件クリア
          </Button>
          <Button size="small" onClick={apply} startIcon={<Icons.Search sx={{ fontSize: 16 }} />}
            disabled={dateReversed} sx={STANDARD_CTA_BUTTON_SX}>
            検索
          </Button>
        </Stack>
      </Popover>
    </>
  );
});

export default TurquoiseFilterPanel;
